import type { GitpodAPI } from "@/api";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useGetAccount } from "@/queries/account-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { useEventInterest } from "@/hooks/use-event-interest";
import { ListSecretsRequest, type Secret, SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";

export type PlainSecret = PlainMessage<Secret>;

export type SecretMountType = Secret["mount"];

export function toPlainSecret(secret: Secret): PlainSecret {
    return toPlainMessage(secret);
}

export const keys = {
    listProjectSecrets: (projectId?: string) => keyWithPrincipal(["secrets", "list", "project", projectId]),
    listUserSecrets: () => keyWithPrincipal(["secrets", "list", "user"]),
    listOrganizationSecrets: (organizationId?: string) =>
        keyWithPrincipal(["secrets", "list", "organization", organizationId]),
};

export const handleProjectSecretEvent = async (_api: GitpodAPI, client: QueryClient, _evt: WatchEventsResponse) => {
    await client.invalidateQueries({ queryKey: keys.listProjectSecrets().slice(0, -1) });
};

export const handleUserSecretEvent = async (_api: GitpodAPI, client: QueryClient, _evt: WatchEventsResponse) => {
    await client.invalidateQueries({ queryKey: keys.listUserSecrets() });
};

export const handleOrganizationSecretEvent = async (
    _api: GitpodAPI,
    client: QueryClient,
    _evt: WatchEventsResponse,
) => {
    await client.invalidateQueries({ queryKey: keys.listOrganizationSecrets().slice(0, -1) });
};

export const useListSecrets = (projectId?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    useEventInterest({ resourceType: ResourceType.SECRET });

    return useQuery({
        queryKey: keys.listProjectSecrets(projectId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            if (!projectId) {
                throw new Error("Project ID is required");
            }

            const secrets = await autoPaginate(
                (params) => api.secretService.listSecrets(params),
                new ListSecretsRequest({
                    filter: {
                        scope: new SecretScope({
                            scope: {
                                value: projectId,
                                case: "projectId",
                            },
                        }),
                    },
                }),
                (response) => response.secrets,
            );

            return {
                secrets: secrets.map(toPlainSecret),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user && !!projectId,
        staleTime: 200,
    });
};

export const useListUserSecrets = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    useEventInterest({ resourceType: ResourceType.USER_SECRET });

    return useQuery({
        queryKey: keys.listUserSecrets(),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const secrets = await autoPaginate(
                (params) => api.secretService.listSecrets(params),
                new ListSecretsRequest({
                    filter: {
                        scope: new SecretScope({
                            scope: {
                                value: user.id,
                                case: "userId",
                            },
                        }),
                    },
                }),
                (response) => response.secrets,
            );

            return {
                secrets: secrets.map(toPlainSecret),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user,
        staleTime: 2000,
    });
};

export const useListOrganizationSecrets = (organizationId?: string) => {
    const api = useGitpodAPI();
    const { data: account } = useGetAccount();

    return useQuery({
        queryKey: keys.listOrganizationSecrets(organizationId),
        queryFn: async () => {
            if (!account) {
                throw new Error("User not authenticated");
            }
            if (!organizationId) {
                throw new Error("Organization ID is required");
            }

            const secrets = await autoPaginate(
                (params) => api.secretService.listSecrets(params),
                new ListSecretsRequest({
                    filter: {
                        scope: new SecretScope({
                            scope: {
                                value: organizationId,
                                case: "organizationId",
                            },
                        }),
                    },
                }),
                (response) => response.secrets,
            );

            return {
                secrets: secrets.map(toPlainSecret),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!account && !!organizationId,
        staleTime: 2000,
    });
};

export const useCreateSecret = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({
            scope,
            secretName,
            plaintextValue,
            mount,
        }: {
            scope: SecretScope;
            secretName: string;
            plaintextValue: string;
            mount: SecretMountType;
        }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const { secret } = await api.secretService.createSecret({
                name: secretName,
                scope,
                value: plaintextValue,
                mount: mount,
            });

            if (secret) {
                setSecretInCache(client, scope, toPlainSecret(secret));
                return toPlainSecret(secret);
            }

            throw new Error("Failed to create secret");
        },
        throwOnError: defaultThrowOnError,
    });
};

export const useUpdateSecretValue = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ secretId, plaintextValue }: { secretId: string; plaintextValue: string }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            await api.secretService.updateSecretValue({
                secretId: secretId,
                value: plaintextValue,
            });
        },
        throwOnError: defaultThrowOnError,
    });
};

export const useDeleteSecret = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ secret }: { secret: PlainSecret }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            await api.secretService.deleteSecret({
                secretId: secret.id,
            });
            removeSecretFromCache(client, secret.scope, secret.id);
        },
        throwOnError: defaultThrowOnError,
    });
};

function removeSecretFromCache(
    client: QueryClient,
    scope?: SecretScope | PlainMessage<SecretScope>,
    secretId?: string,
) {
    if (!scope || !secretId) {
        return;
    }
    let querykey;
    switch (scope.scope.case) {
        case "projectId":
            querykey = keys.listProjectSecrets(scope.scope.value);
            break;
        case "userId":
            querykey = keys.listUserSecrets();
            break;
        case "organizationId":
            querykey = keys.listOrganizationSecrets(scope.scope.value);
            break;
        default:
            return;
    }
    client.setQueryData(querykey, (currentData?: { secrets: PlainSecret[] }) => {
        if (!currentData) {
            return [];
        }

        return {
            ...currentData,
            secrets: currentData.secrets.filter((s) => s?.id !== secretId),
        };
    });
}

function setSecretInCache(client: QueryClient, scope: SecretScope, secret: PlainSecret) {
    let querykey;
    switch (scope.scope.case) {
        case "projectId":
            querykey = keys.listProjectSecrets(scope.scope.value);
            break;
        case "userId":
            querykey = keys.listUserSecrets();
            break;
        case "organizationId":
            querykey = keys.listOrganizationSecrets(scope.scope.value);
            break;
        default:
            return;
    }
    client.setQueryData(querykey, (currentData?: { secrets: PlainSecret[] }) => {
        if (!currentData) {
            return [secret];
        }

        return {
            ...currentData,
            secrets: currentData.secrets
                .filter((s) => s?.id !== secret.id)
                .concat([secret])
                .filter((w) => !!w),
        };
    });
}
